import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import analyticsService from '../services/analyticsService';
import authService from '../services/authService';
import Layout from '../components/Layout';
import AssistantModeSwitch from '../components/AssistantModeSwitch';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Customized
} from 'recharts';
import ChartPointLabels from '../components/dashboard/ChartPointLabels';
import CountUpNumber from '../components/dashboard/CountUpNumber';
import { DashboardData, TopService } from '../types';
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "@/lib/utils";

// Shadcn UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { MonthPicker } from "@/components/ui/month-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Icons
import {
  Calendar as CalendarIcon,
  TrendingUp,
  Euro,
  Users,
  Clock,
  CalendarDays,
  Copy,
  AlertCircle,
  Phone,
  MessageCircle,
  RefreshCw
} from "lucide-react";

// Official TimeGlobe brand mark (same asset as the sidebar logo), used as watermark.
const TG_AI_LOGO_SRC = `${process.env.PUBLIC_URL}/images/timeglobe.svg`;

// Tab type for switching between Chat and Voice views
type DashboardTab = 'all' | 'chat' | 'voice';

// Mindestdauer der Refresh-Animation, damit sie auch bei schnellen Antworten sichtbar bleibt
const MIN_REFRESH_DURATION_MS = 550;

// Anzeigenamen der Chart-Linien, genutzt fuer die Labels direkt am Datenpunkt
const CHART_SERIES_LABELS: Record<string, string> = {
  count: 'Termine',
  services: 'Services',
  cancelled: 'Storniert',
};

const DashboardPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [monthLoading, setMonthLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [activeCalendarType, setActiveCalendarType] = useState<'today' | 'services' | 'month' | null>(null);
  const [accountCreationDate, setAccountCreationDate] = useState<Date | null>(null);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [topServices, setTopServices] = useState<TopService[]>([]);
  const [topServicesLoading, setTopServicesLoading] = useState(false);
  const [topServicesError, setTopServicesError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DashboardTab>('all');
  const [tabLoading, setTabLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Steigt nach jedem Refresh – laesst die Zahlen erneut hochzaehlen, auch
  // wenn sich der Wert selbst nicht geaendert hat.
  const [refreshTick, setRefreshTick] = useState(0);
  const refreshTimeoutRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
    }
  }, []);

  const fetchTopServices = useCallback(async () => {
    try {
      setTopServicesLoading(true);
      setTopServicesError(null);
      
      let source: string | undefined;
      if (activeTab === 'chat') source = 'whatsapp';
      else if (activeTab === 'voice') source = 'voice';

      const services = await analyticsService.getTopServices(source);
      setTopServices(services);
    } catch (err) {
      console.error('Fehler beim Laden der Top-Dienstleistungen:', err);
      setTopServices([]);
      setTopServicesError('Fehler beim Laden der Top-Dienstleistungen');
    } finally {
      setTopServicesLoading(false);
    }
  }, [activeTab]);

  /**
   * Manuelles Neuladen: laesst nur die Werte kurz verschwinden und wieder
   * einspringen – Karten, Ueberschriften und Layout bleiben stehen.
   */
  const handleRefresh = useCallback(async () => {
    if (refreshing || tabLoading || loading) return;

    setRefreshing(true);
    const startedAt = Date.now();

    let source: string | undefined;
    if (activeTab === 'chat') source = 'whatsapp';
    else if (activeTab === 'voice') source = 'voice';

    try {
      const [monthlyData] = await Promise.all([
        analyticsService.getMonthlyAnalytics(selectedMonth, source),
        fetchTopServices(),
      ]);
      setDashboardData(monthlyData);
      setError(null);
    } catch (err) {
      console.error('Fehler beim Aktualisieren der Dashboard-Daten:', err);
      setError('Fehler beim Aktualisieren der Dashboard-Daten');
    } finally {
      // Mindestdauer, damit die Ausblend-Animation bei schnellen Antworten
      // nicht abgehackt wirkt.
      const remaining = Math.max(0, MIN_REFRESH_DURATION_MS - (Date.now() - startedAt));
      refreshTimeoutRef.current = window.setTimeout(() => {
        setRefreshing(false);
        setRefreshTick((tick) => tick + 1);
        refreshTimeoutRef.current = null;
      }, remaining);
    }
  }, [refreshing, tabLoading, loading, activeTab, selectedMonth, fetchTopServices]);

  // Handle tab change with loading animation
  const handleTabChange = useCallback(async (newTab: DashboardTab) => {
    if (newTab === activeTab) return;
    
    setTabLoading(true);
    setActiveTab(newTab);
  }, [activeTab]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        // Only show full loading on initial load, not tab switches
        if (!dashboardData) {
          setLoading(true);
        }
        
        const businessProfile = await authService.getBusinessProfile();
        if (businessProfile.created_at) {
          setAccountCreationDate(new Date(businessProfile.created_at));
        }

        try {
          const dateRanges = await analyticsService.getAvailableDateRanges();
          if (dateRanges && dateRanges.available_dates) {
            setAvailableDates(dateRanges.available_dates);
          } else {
            setAvailableDates([]);
          }
        } catch (error) {
          console.error('Fehler beim Laden der verfügbaren Datumsbereiche:', error);
          setAvailableDates([]);
        }

        let source: string | undefined;
        if (activeTab === 'chat') source = 'whatsapp';
        else if (activeTab === 'voice') source = 'voice';

        const monthlyData = await analyticsService.getMonthlyAnalytics(selectedMonth, source);
        setDashboardData(monthlyData);
        await fetchTopServices();
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Fehler beim Laden der monatlichen Daten');
      } finally {
        setLoading(false);
        setTabLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, navigate, selectedMonth, fetchTopServices, activeTab]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const intervalId = setInterval(() => {
      let source: string | undefined;
      if (activeTab === 'chat') source = 'whatsapp';
      else if (activeTab === 'voice') source = 'voice';

      // Nur Dashboard-Daten aktualisieren, nicht Top-Services (um unnötiges Neuladen zu vermeiden)
      analyticsService
        .getMonthlyAnalytics(selectedMonth, source)
        .then((data) => setDashboardData(data))
        .catch((err) => console.error('Error refreshing dashboard data:', err));
    }, 30000);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, selectedMonth, activeTab]);

  const isDateAvailable = (date: Date) => {
    if (!availableDates.length) return true;
    const dateString = date.toISOString().split('T')[0];
    return availableDates.includes(dateString);
  };

  const handleDateSelect = async (newDate: Date | undefined, type: 'today' | 'services' | 'month') => {
    if (!newDate) return;

    try {
      if (type === 'month') {
        setMonthLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);

      if (type !== 'month') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (newDate > today) {
           // Shadcn Calendar disables future dates via disabled prop, so this check is redundant but safe
           throw new Error('Bitte wählen Sie ein Datum in der Vergangenheit oder heute aus');
        }
      }

      switch (type) {
        case 'today':
          setSelectedDate(newDate);
          const appointmentsData = await analyticsService.getAppointmentsForDate(newDate);
          if (appointmentsData && appointmentsData.summary) {
            setDashboardData(prev => prev ? ({
              ...prev,
              summary: {
                ...prev.summary,
                today_appointments: appointmentsData.summary.today_appointments
              }
            }) : null);
          }
          break;

        case 'services':
          setSelectedDate(newDate);
          const servicesData = await analyticsService.getServicesForDate(newDate);
          if (servicesData && servicesData.summary) {
            setDashboardData(prev => prev ? ({
              ...prev,
              summary: {
                ...prev.summary,
                todays_services: servicesData.summary.todays_services
              }
            }) : null);
          }
          break;

        case 'month':
          setSelectedMonth(newDate);
          const monthlyData = await analyticsService.getMonthlyAnalytics(newDate);
          setDashboardData(monthlyData);
          break;
      }
    } catch (error) {
      console.error('Fehler:', error);
      setError(error instanceof Error ? error.message : 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
      setMonthLoading(false);
      setActiveCalendarType(null); // Close popover
    }
  };

  // Stats values
  const todayAppointmentsEnd = dashboardData?.summary?.today_appointments || 0;

  const todayCancelledEnd = dashboardData?.summary?.today_cancelled || 0;

  const servicesBookedTodayEnd = dashboardData?.summary?.todays_services || 0;

  const monthlyCost = dashboardData?.summary?.costs_last_30_days || 0;
  const dailyCost = dashboardData?.summary?.costs_today || 0;

  const thirtyDayAppointmentsEnd = dashboardData?.summary?.monthly_appointments || 0;

  const monthlyCancelledEnd = dashboardData?.summary?.monthly_cancelled || 0;

  const monthlyServicesBookedEnd = dashboardData?.summary?.monthly_services_booked || 0;

  const getDayTitle = (type: string, date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return type === 'appointments' ? 'Termine Heute' : 'Leistungen Heute';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return type === 'appointments' ? 'Termine Gestern' : 'Leistungen Gestern';
    } else {
      return `${type === 'appointments' ? 'Termine' : 'Leistungen'} ${format(date, 'dd.MM.yy', { locale: de })}`;
    }
  };

  const getMonthTitle = (date: Date) => {
    const today = new Date();
    if (date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()) {
      return 'Aktueller Monat';
    }
    return format(date, 'MMMM yyyy', { locale: de });
  };
  
  const formatServiceName = (name: string) => {
    return name.replace(/^\d+\.\s*/, '').trim();
  };
  
  const formatDateTime = (dateString: string, timeString: string) => {
    if (!dateString || !timeString) return '';
    const date = new Date(`${dateString}T${timeString}`);
    return format(date, "dd.MM.yy, HH:mm", { locale: de });
  };

  if (loading && !dashboardData) {
    return (
      <Layout>
        <div className="flex flex-col justify-center items-center h-[calc(100vh-200px)] gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-primary/25 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full absolute top-0 left-0 animate-spin"></div>
          </div>
          <p className="text-muted-foreground text-sm animate-pulse">Dashboard wird geladen...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="relative">
        {/* Lade-Overlay beim Monatswechsel */}
        {monthLoading && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex flex-col justify-center items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-primary/25 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full absolute top-0 left-0 animate-spin"></div>
            </div>
            <p className="text-muted-foreground text-sm animate-pulse">Monatsdaten werden geladen...</p>
          </div>
        )}

        {/* Lade-Overlay beim Tab-Wechsel */}
        {tabLoading && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex flex-col justify-center items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-primary/25 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full absolute top-0 left-0 animate-spin"></div>
            </div>
            <p className="text-muted-foreground text-sm animate-pulse">
              {activeTab === 'voice' ? 'Telefon-Termine werden geladen...' : 
               activeTab === 'chat' ? 'Chat-Termine werden geladen...' : 
               'Alle Termine werden geladen...'}
            </p>
          </div>
        )}

        {/* TimeGlobe Wasserzeichen – dezent im Hintergrund (hell auf dunkel, dunkel auf hell).
            absolute statt fixed: zentriert im Inhaltsbereich (rechts der Sidebar), nicht im Viewport. */}
        <div className="absolute inset-0 pointer-events-none z-0 opacity-[0.03] flex items-center justify-center overflow-hidden">
           <img
             src={TG_AI_LOGO_SRC}
             alt=""
             aria-hidden
             className="w-[80vh] h-[80vh] object-contain [filter:brightness(0)] dark:[filter:brightness(0)_invert(1)]"
           />
        </div>

        <div className={cn("relative z-10 space-y-8", refreshing && "dash-refreshing")}>
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Links: Titel */}
            <div className="flex justify-start md:flex-1">
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
                Dashboard
              </h1>
            </div>

            {/* Mitte: Pill Dashboard <-> TimeGlobe Intelligence (nur wenn freigeschaltet) */}
            <div className="flex justify-center md:flex-1">
              <AssistantModeSwitch active="dashboard" />
            </div>

            {/* Rechts: Dashboard-Steuerung */}
            <div className="flex justify-center md:justify-end md:flex-1">
            <div className="flex items-center gap-2">
            {/* Dashboard-Daten neu laden */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing || tabLoading || loading}
              aria-label="Dashboard-Daten aktualisieren"
              title="Dashboard-Daten aktualisieren"
              className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <RefreshCw className={cn("h-4 w-4 transition-transform", refreshing && "animate-spin")} />
            </Button>

            {/* Tab Navigation: Chat / Telefon */}
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <Button
                variant={activeTab === 'all' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleTabChange('all')}
                disabled={tabLoading}
                className={cn(
                  "gap-2 transition-all",
                  activeTab === 'all'
                    ? "bg-card shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Alle
              </Button>
              <Button
                variant={activeTab === 'chat' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleTabChange('chat')}
                disabled={tabLoading}
                className={cn(
                  "gap-2 transition-all",
                  activeTab === 'chat'
                    ? "bg-card shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <MessageCircle className="h-4 w-4" />
                Chat
              </Button>
              <Button
                variant={activeTab === 'voice' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleTabChange('voice')}
                disabled={tabLoading}
                className={cn(
                  "gap-2 transition-all",
                  activeTab === 'voice'
                    ? "bg-card shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Phone className="h-4 w-4" />
                Telefon
              </Button>
            </div>
            </div>
            </div>
          </div>

          <>
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
            
            {/* Termine Heute */}
            <Card className="hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Termine Heute
                </CardTitle>
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline justify-between mt-2">
                  <div>
                    <div className="dash-value text-3xl font-bold text-primary">
                      <CountUpNumber end={todayAppointmentsEnd} active={!!dashboardData} restartKey={refreshTick} />
                    </div>
                    <p className="text-xs text-muted-foreground">Bestätigt</p>
                  </div>
                  <div className="text-right">
                    <div className="dash-value text-2xl font-semibold text-cancelled-strong">
                      <CountUpNumber end={todayCancelledEnd} active={!!dashboardData} restartKey={refreshTick} align="right" />
                    </div>
                    <p className="text-xs text-muted-foreground">Storniert</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Leistungen Heute */}
            <Card className="hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Leistungen Heute
                </CardTitle>
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                 <div className="flex items-baseline mt-2">
                    <div className="dash-value text-3xl font-bold text-primary">
                      <CountUpNumber end={servicesBookedTodayEnd} active={!!dashboardData} restartKey={refreshTick} />
                    </div>
                 </div>
                 <p className="text-xs text-muted-foreground mt-1">Gebuchte Services</p>
              </CardContent>
            </Card>

            {/* Kosten */}
            <Card className="hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Kosten</CardTitle>
                <Euro className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline justify-between mt-2">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Monat</p>
                    <div className="dash-value text-2xl font-bold text-primary">
                      {monthlyCost.toFixed(2).replace('.', ',')} €
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground mb-1">Heute</p>
                    <div className="dash-value text-xl font-semibold text-primary-strong">
                       {dailyCost.toFixed(2).replace('.', ',')} €
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Top Services */}
            <Card className="hover:shadow-lg transition-all duration-200 hover:-translate-y-1 overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Top 3 Dienstleistungen</CardTitle>
                <TrendingUp className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent className="pt-2">
                {topServicesLoading && !refreshing ? (
                   <div className="space-y-2">
                     <Skeleton className="h-4 w-full" />
                     <Skeleton className="h-4 w-full" />
                     <Skeleton className="h-4 w-full" />
                   </div>
                ) : topServicesError ? (
                  <div className="text-xs text-destructive">{topServicesError}</div>
                ) : topServices.length > 0 ? (
                  <div className="dash-value space-y-2">
                    {topServices.map((service, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 truncate">
                          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-[10px] font-bold text-primary-strong">
                            {index + 1}
                          </div>
                          <span className="font-medium truncate max-w-[120px]" title={service.service_name}>
                             {formatServiceName(service.service_name)}
                          </span>
                        </div>
                        <div className="flex flex-col items-end">
                           <span className="font-bold text-primary">{service.booking_count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">Noch keine Buchungen.</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Content Area: Chart + Activities */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
             {/* Chart Section */}
             <Card className="xl:col-span-2 shadow-md">
               <CardHeader className="flex flex-row items-center justify-between">
                 <div>
                   <CardTitle>{getMonthTitle(selectedMonth)}</CardTitle>
                   <CardDescription>
                     Wachstum: <span className={cn("dash-value font-medium", (dashboardData?.summary?.monthly_growth_rate || 0) >= 0 ? "text-brand-strong" : "text-cancelled-strong")}>
                        {dashboardData?.summary?.monthly_growth_rate}%
                     </span>
                   </CardDescription>
                 </div>
                 <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="ml-auto">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      Monat wählen
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <MonthPicker
                      selected={selectedMonth}
                      onSelect={(date) => handleDateSelect(date, 'month')}
                    />
                  </PopoverContent>
                </Popover>
               </CardHeader>
               <CardContent>
                  <div className="flex gap-8 mb-6">
                    <div>
                      <div className="text-sm text-muted-foreground">Termine</div>
                      <div className="flex items-baseline gap-2">
                         <span className="dash-value text-2xl font-bold text-primary">
                           <CountUpNumber end={thirtyDayAppointmentsEnd} active={!!dashboardData} restartKey={refreshTick} />
                         </span>
                         <span className="text-xs text-muted-foreground">Bestätigt</span>
                      </div>
                      <div className="dash-value text-sm font-medium text-cancelled-strong mt-1">
                        <CountUpNumber end={monthlyCancelledEnd} active={!!dashboardData} restartKey={refreshTick} /> <span className="text-xs text-muted-foreground font-normal">Storniert</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Gebuchte Services</div>
                      <div className="dash-value text-2xl font-bold text-primary">
                        <CountUpNumber end={monthlyServicesBookedEnd} active={!!dashboardData} restartKey={refreshTick} />
                      </div>
                    </div>
                  </div>

                  <div className="dash-chart h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={dashboardData?.appointment_time_series || []}
                        margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--chart-grid))" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(date) => format(new Date(date), 'dd.MM')}
                          stroke="hsl(var(--chart-axis))"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis 
                          stroke="hsl(var(--chart-axis))"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(value) => Math.round(value).toString()}
                        />
                        {/* Kein Tooltip-Block: die Werte stehen einzeln am jeweiligen Punkt (ChartPointLabels).
                            Der Tooltip bleibt nur als unsichtbarer Trigger fuer Cursor + aktiven Punkt. */}
                        <RechartsTooltip
                          content={() => null}
                          cursor={{ stroke: 'hsl(var(--chart-axis))', strokeWidth: 1, strokeDasharray: '3 3' }}
                        />
                        <Line type="monotone" dataKey="count" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}} />
                        <Line type="monotone" dataKey="services" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}} />
                        <Line type="monotone" dataKey="cancelled" stroke="hsl(var(--chart-3))" strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}} />
                        <Customized
                          component={(props: any) => (
                            <ChartPointLabels
                              {...props}
                              labels={CHART_SERIES_LABELS}
                            />
                          )}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
               </CardContent>
             </Card>

             {/* Recent Activities */}
             <Card className="shadow-md flex flex-col h-full max-h-[500px] xl:max-h-auto">
               <CardHeader>
                 <CardTitle>Letzte Aktivitäten</CardTitle>
               </CardHeader>
               <CardContent className="flex-1 overflow-y-auto pr-3">
                 {(() => {
                   // No client-side filtering needed anymore, API returns filtered data
                   const filteredAppointments = dashboardData?.recent_appointments || [];
                   
                   return filteredAppointments.length > 0 ? (
                     <div className="dash-value space-y-4">
                       {filteredAppointments.slice(0, 10).map((activity, i) => {
                         const normalizeName = (name: string) => name.replace(/^\d{1,2}\.\s*/, '').trim();
                         const rawServiceNames = activity.service_names?.length ? activity.service_names : [activity.service_name];
                         const serviceNames = Array.from(new Set(rawServiceNames.map(normalizeName).filter(Boolean)));
                         const isVoice = activity.booking_source === 'voice';
                         const isWhatsapp = activity.booking_source === 'whatsapp';
                         const isTest = activity.is_test_call === true;
                         const phoneDigits = (activity.customer_phone || '').replace(/\D/g, '');
                         // Only bot channels have a stored conversation to open.
                         const canOpen = phoneDigits.length > 0 && (isVoice || isWhatsapp);
                         const openConversation = () => {
                           if (!canOpen) return;
                           const channel = isWhatsapp ? 'whatsapp' : 'phone';
                           const at = activity.appointment_date && activity.appointment_time
                             ? `${activity.appointment_date}T${activity.appointment_time}`
                             : '';
                           const params = new URLSearchParams({ phone: phoneDigits, type: channel });
                           if (at) params.set('at', at);
                           navigate(`/conversations?${params.toString()}`);
                         };

                         return (
                           <div
                             key={activity.booking_id}
                             onClick={openConversation}
                             role={canOpen ? 'button' : undefined}
                             tabIndex={canOpen ? 0 : undefined}
                             onKeyDown={
                               canOpen
                                 ? (e) => {
                                     if (e.key === 'Enter' || e.key === ' ') {
                                       e.preventDefault();
                                       openConversation();
                                     }
                                   }
                                 : undefined
                             }
                             title={canOpen ? 'Gesprächsverlauf öffnen' : undefined}
                             className={cn(
                               "p-3 rounded-lg border transition-all hover:shadow-sm",
                               canOpen && "cursor-pointer hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-ring/40",
                               activity.status === 'cancelled'
                                 ? "bg-cancelled/10 border-cancelled/30"
                                 : isTest
                                   ? "bg-muted border-border opacity-60"
                                   : "bg-card border-border"
                             )}
                           >
                             <div className="flex items-start justify-between mb-1 gap-2">
                               <h4 className={cn("font-medium text-sm flex-1 min-w-0 break-words", activity.status === 'cancelled' && "line-through text-cancelled-strong")}>
                                 {serviceNames.join(', ')}
                               </h4>
                               <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
                                 {isTest && (
                                   <div className="flex items-center text-muted-foreground text-[10px] font-semibold uppercase tracking-wide bg-muted px-2 py-0.5 rounded-full border border-border">
                                     Test
                                   </div>
                                 )}
                                 {isVoice && (
                                   <div className="flex items-center text-primary-strong text-[10px] font-medium bg-primary/10 px-1.5 py-0.5 rounded border border-primary/25">
                                     <Phone className="h-3 w-3 mr-1" />
                                     Telefon
                                   </div>
                                 )}
                                 {isWhatsapp && (
                                   <div className="flex items-center text-success-strong text-[10px] font-medium bg-success/10 px-1.5 py-0.5 rounded border border-success/25">
                                     <svg viewBox="0 0 24 24" className="h-3 w-3 mr-1" fill="currentColor" aria-hidden="true">
                                       <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                     </svg>
                                     WhatsApp
                                   </div>
                                 )}
                                 {activity.status === 'cancelled' && (
                                   <Badge variant="outline" className="bg-cancelled/15 text-cancelled-strong border-cancelled/30 text-[10px] px-1 h-5">Storniert</Badge>
                                 )}
                               </div>
                             </div>
                             
                             <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                               <div className={cn(
                                 "flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold",
                                 isWhatsapp
                                   ? "bg-success/15 text-success-strong"
                                   : "bg-primary/15 text-primary-strong"
                               )}>
                                 {activity.customer_name.charAt(0).toUpperCase()}
                               </div>
                               <span>{activity.customer_name}</span>
                             </div>

                             <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                <span>{formatDateTime(activity.appointment_date, activity.appointment_time)} Uhr</span>
                             </div>
                           </div>
                         );
                       })}
                     </div>
                   ) : (
                     <div className="h-full flex flex-col items-center justify-center text-muted-foreground p-6">
                       <Clock className="h-10 w-10 mb-2 opacity-20" />
                       <p className="text-sm">
                         {activeTab === 'voice' ? 'Keine Telefon-Termine' : 
                          activeTab === 'chat' ? 'Keine Chat-Termine' : 
                          'Keine aktuellen Aktivitäten'}
                       </p>
                     </div>
                   );
                 })()}
               </CardContent>
             </Card>
          </div>
          </>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
